import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API_URL } from '../utils/api';
import { offlineStorage } from '../utils/offlineStorage';

// Helper to get session token from localStorage
const getSessionToken = () => {
  return localStorage.getItem('mentis_session_token');
};

// Helper to make authenticated fetch requests
// Handles: network errors, non-JSON responses, auth token injection
const authFetch = async (url, options = {}) => {
  const token = getSessionToken();
  const headers = {
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });
    return response;
  } catch (error) {
    // Network error — return a synthetic Response so callers can use .ok check
    console.warn('[authFetch] Network error:', error.message);
    throw error;
  }
};

// Session refresh interval (check every 30 minutes)
let sessionRefreshInterval = null;

// Auth Store with Production Hardening - Token-based auth for cross-domain
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      sessionStatus: null,
      lastAuthCheck: null,
      
      setUser: (user) => set({ 
        user, 
        isAuthenticated: !!user, 
        isLoading: false,
        lastAuthCheck: Date.now()
      }),
      
      setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
      
      checkAuth: async (forceCheck = false) => {
        const state = get();
        
        // Check if we have a token
        const token = getSessionToken();
        
        // If no token and not forcing, return null quickly
        if (!token && !forceCheck) {
          console.log('[AuthStore] No token found');
          set({ user: null, isAuthenticated: false, isLoading: false });
          return null;
        }
        
        // If we checked recently AND have cached user, return cached result
        if (!forceCheck && state.lastAuthCheck && Date.now() - state.lastAuthCheck < 2000 && state.user) {
          console.log('[AuthStore] Using cached auth state');
          return state.user;
        }
        
        set({ isLoading: true });
        
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          
          const response = await authFetch(`${API_URL}/auth/me`, {
            signal: controller.signal,
            headers: {
              'Cache-Control': 'no-cache'
            }
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok) {
            let user;
            try {
              user = await response.json();
            } catch {
              console.warn('[AuthStore] Invalid JSON from /auth/me');
              set({ isLoading: false });
              return get().user || null;
            }
            console.log('[AuthStore] Auth check successful:', user.email);
            set({ 
              user, 
              isAuthenticated: true, 
              isLoading: false,
              lastAuthCheck: Date.now()
            });
            get().startSessionMonitor();
            return user;
          } else {
            console.log('[AuthStore] Auth check failed: not authenticated');
            // Clear invalid token
            localStorage.removeItem('mentis_session_token');
            set({ 
              user: null, 
              isAuthenticated: false, 
              isLoading: false,
              lastAuthCheck: Date.now()
            });
            get().stopSessionMonitor();
            return null;
          }
        } catch (error) {
          console.error('[AuthStore] checkAuth error:', error.name === 'AbortError' ? 'Timeout' : error.message);
          // CRITICAL FIX: On network error, preserve existing persisted auth state.
          // Don't log the user out just because the network is temporarily unavailable
          // (common on PWA startup before connection is fully established).
          const currentState = get();
          if (currentState.user && currentState.isAuthenticated) {
            console.log('[AuthStore] Network error at startup, using cached auth state');
            set({ isLoading: false });
            return currentState.user;
          }
          set({ 
            user: null, 
            isAuthenticated: false, 
            isLoading: false,
            lastAuthCheck: Date.now()
          });
          return null;
        }
      },
      
      checkSessionStatus: async () => {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          
          // CRITICAL FIX: Use authFetch (includes Bearer token header)
          // Plain fetch with cookies-only fails in PWA standalone mode (iOS)
          const response = await authFetch(`${API_URL}/auth/session-status`, {
            signal: controller.signal,
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok) {
            let status;
            try {
              status = await response.json();
            } catch {
              console.warn('[AuthStore] Invalid JSON from /auth/session-status');
              return null;
            }
            set({ sessionStatus: status });
            
            // Auto-refresh if needed
            if (status.authenticated && status.needs_refresh) {
              console.log('[AuthStore] Session needs refresh');
              await get().refreshSession();
            }
            
            // Handle expired session
            if (!status.authenticated) {
              console.log('[AuthStore] Session expired');
              set({ user: null, isAuthenticated: false });
              get().stopSessionMonitor();
            }
            
            return status;
          }
          return null;
        } catch (error) {
          console.error('[AuthStore] checkSessionStatus error:', error.message);
          return null;
        }
      },
      
      refreshSession: async () => {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          
          // CRITICAL FIX: Use authFetch (includes Bearer token header)
          const response = await authFetch(`${API_URL}/auth/refresh`, {
            method: 'POST',
            signal: controller.signal,
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok) {
            const data = await response.json();
            console.log('[AuthStore] Session refreshed successfully');
            return data;
          }
          return null;
        } catch (error) {
          console.error('[AuthStore] Session refresh failed:', error.message);
          return null;
        }
      },
      
      startSessionMonitor: () => {
        if (sessionRefreshInterval) return;
        
        console.log('[AuthStore] Starting session monitor');
        
        // Check session status every 25 minutes (before typical 30-min expiry warning)
        sessionRefreshInterval = setInterval(() => {
          const state = get();
          if (state.isAuthenticated) {
            get().checkSessionStatus();
          }
        }, 25 * 60 * 1000);
        
        // Initial check after a short delay
        setTimeout(() => get().checkSessionStatus(), 5000);
      },
      
      stopSessionMonitor: () => {
        if (sessionRefreshInterval) {
          console.log('[AuthStore] Stopping session monitor');
          clearInterval(sessionRefreshInterval);
          sessionRefreshInterval = null;
        }
      },
      
      processSession: async (sessionId) => {
        console.log('[AuthStore] Processing session');
        set({ isLoading: true });
        
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000);
          
          const response = await fetch(`${API_URL}/auth/session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sessionId }),
            credentials: 'include',
            signal: controller.signal,
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok) {
            let data;
            try {
              data = await response.json();
            } catch {
              console.warn('[AuthStore] Invalid JSON from /auth/session');
              set({ isLoading: false });
              return null;
            }
            console.log('[AuthStore] Session processed successfully:', data.user?.email);
            
            // CRITICAL: Set auth state immediately and clear any cache
            set({ 
              user: data.user, 
              isAuthenticated: true, 
              isLoading: false,
              lastAuthCheck: Date.now()
            });
            
            get().startSessionMonitor();
            return data.user;
          } else {
            const errorData = await response.json().catch(() => ({}));
            console.error('[AuthStore] Session processing failed:', errorData);
            set({ isLoading: false });
            return null;
          }
        } catch (error) {
          console.error('[AuthStore] processSession error:', error.message);
          set({ isLoading: false });
          return null;
        }
      },
      
      // Force check auth without cache - used after session creation
      forceCheckAuth: async () => {
        console.log('[AuthStore] Force checking auth (no cache)');
        set({ isLoading: true, lastAuthCheck: null });
        
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          
          const response = await authFetch(`${API_URL}/auth/me`, {
            signal: controller.signal,
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok) {
            let user;
            try {
              user = await response.json();
            } catch {
              console.warn('[AuthStore] Invalid JSON from /auth/me (force)');
              set({ isLoading: false });
              return null;
            }
            console.log('[AuthStore] Force check successful:', user.email);
            set({ 
              user, 
              isAuthenticated: true, 
              isLoading: false,
              lastAuthCheck: Date.now()
            });
            return user;
          } else {
            console.log('[AuthStore] Force check failed: not authenticated');
            set({ 
              user: null, 
              isAuthenticated: false, 
              isLoading: false,
              lastAuthCheck: Date.now()
            });
            return null;
          }
        } catch (error) {
          console.error('[AuthStore] Force check error:', error.message);
          set({ 
            user: null, 
            isAuthenticated: false, 
            isLoading: false,
            lastAuthCheck: Date.now()
          });
          return null;
        }
      },
      
      logout: async () => {
        console.log('[AuthStore] Logging out');
        get().stopSessionMonitor();
        
        // Clear token from localStorage
        localStorage.removeItem('mentis_session_token');
        
        try {
          await authFetch(`${API_URL}/auth/logout`, {
            method: 'POST',
          });
        } catch (error) {
          console.error('[AuthStore] Logout request failed:', error.message);
        }
        
        set({ 
          user: null, 
          isAuthenticated: false,
          sessionStatus: null,
          lastAuthCheck: null
        });
        
        // Clear usage store so stale free/premium data doesn't bleed into next session
        try { useUsageStore.getState().clearUsage(); } catch (e) {}
        
        // Clear persisted storage
        try {
          localStorage.removeItem('mentis-auth');
        } catch (e) {}
      },
      
      updateRole: async (userId, role) => {
        try {
          const response = await authFetch(`${API_URL}/auth/update-role`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: userId, role }),
          });
          return response.ok;
        } catch (error) {
          console.error('[AuthStore] updateRole error:', error.message);
          return false;
        }
      },
      
      // Force clear all auth state (useful for debugging)
      clearAuth: () => {
        get().stopSessionMonitor();
        set({ 
          user: null, 
          isAuthenticated: false, 
          isLoading: false,
          sessionStatus: null,
          lastAuthCheck: null
        });
        try {
          localStorage.removeItem('mentis-auth');
        } catch (e) {}
      },
    }),
    {
      name: 'mentis-auth',
      partialize: (state) => ({ 
        user: state.user, 
        isAuthenticated: state.isAuthenticated,
        lastAuthCheck: state.lastAuthCheck
      }),
    }
  )
);

// Export authFetch for use in other components
export { authFetch, getSessionToken };

// Theme Store
export const useThemeStore = create(
  persist(
    (set) => ({
      theme: 'light',
      toggleTheme: () => set((state) => ({ 
        theme: state.theme === 'light' ? 'dark' : 'light' 
      })),
      setTheme: (theme) => set({ theme }),
    }),
    { name: 'mentis-theme' }
  )
);

// App Store
export const useAppStore = create((set) => ({
  sidebarOpen: true,
  activePage: 'dashboard',
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setActivePage: (page) => set({ activePage: page }),
}));

// Progress Store
export const useProgressStore = create((set) => ({
  progress: null,
  analytics: null,
  isLoading: false,
  
  fetchProgress: async () => {
    set({ isLoading: true });
    try {
      const response = await authFetch(`${API_URL}/progress`, { credentials: 'include' });
      if (response.ok) {
        let data;
        try {
          data = await response.json();
        } catch {
          console.warn('[ProgressStore] Invalid JSON from /progress');
          set({ isLoading: false });
          return null;
        }
        set({ progress: data.progress, isLoading: false });
        return data.progress;
      }
    } catch (error) {
      console.warn('[ProgressStore] fetchProgress error:', error.message);
    }
    set({ isLoading: false });
    return null;
  },
  
  fetchAnalytics: async () => {
    set({ isLoading: true });
    try {
      const response = await authFetch(`${API_URL}/analytics/user`, { credentials: 'include' });
      if (response.ok) {
        let data;
        try {
          data = await response.json();
        } catch {
          console.warn('[ProgressStore] Invalid JSON from /analytics/user');
          set({ isLoading: false });
          return null;
        }
        set({ analytics: data.analytics, isLoading: false });
        return data.analytics;
      }
    } catch (error) {
      console.warn('[ProgressStore] fetchAnalytics error:', error.message);
    }
    set({ isLoading: false });
    return null;
  },
}));

// Teacher Store
export const useTeacherStore = create((set, get) => ({
  classes: [],
  selectedClass: null,
  assignments: [],
  submissions: [],
  isLoading: false,
  
  fetchClasses: async () => {
    set({ isLoading: true });
    try {
      const response = await authFetch(`${API_URL}/classes`);
      if (response.ok) {
        const data = await response.json();
        set({ classes: data.classes, isLoading: false });
        return data.classes;
      }
    } catch (error) {}
    set({ isLoading: false });
    return [];
  },
  
  createClass: async (name, description, subject) => {
    try {
      const response = await authFetch(`${API_URL}/classes/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description, subject }),
      });
      if (response.ok) {
        const data = await response.json();
        set((state) => ({ classes: [...state.classes, data.class] }));
        return data.class;
      }
    } catch (error) {}
    return null;
  },
  
  joinClass: async (inviteCode) => {
    try {
      const response = await authFetch(`${API_URL}/classes/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invite_code: inviteCode }),
      });
      if (response.ok) {
        const data = await response.json();
        return data;
      }
    } catch (error) {}
    return null;
  },
  
  setSelectedClass: (cls) => set({ selectedClass: cls }),
  
  fetchAssignments: async (classId) => {
    try {
      const response = await authFetch(`${API_URL}/assignments/${classId}`);
      if (response.ok) {
        const data = await response.json();
        set({ assignments: data.assignments });
        return data.assignments;
      }
    } catch (error) {}
    return [];
  },
  
  createAssignment: async (assignment) => {
    try {
      const response = await authFetch(`${API_URL}/assignments/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assignment),
      });
      if (response.ok) {
        const data = await response.json();
        set((state) => ({ assignments: [...state.assignments, data.assignment] }));
        return data.assignment;
      }
    } catch (error) {}
    return null;
  },
}));

// Flashcard Store — with offline support

export const useFlashcardStore = create((set, get) => ({
  cards: [],
  dueCards: [],
  stats: null,
  isLoading: false,
  isOffline: false,
  pendingReviewCount: 0,

  _getUserId: () => useAuthStore.getState().user?.user_id,

  fetchDueCards: async () => {
    set({ isLoading: true });
    try {
      const response = await authFetch(`${API_URL}/flashcards/due`);
      if (response.ok) {
        const data = await response.json();
        // Cache for offline use
        const userId = get()._getUserId();
        if (userId) offlineStorage.saveFlashcards(userId, data.cards);
        set({ dueCards: data.cards, isLoading: false, isOffline: false });
        return data.cards;
      }
    } catch {
      // Network error — try offline cache
      const userId = get()._getUserId();
      const cached = userId ? offlineStorage.loadFlashcards(userId) : [];
      const pending = userId ? offlineStorage.getPendingReviews(userId).length : 0;
      // Always mark as offline when network fails (even if no cached cards)
      set({ dueCards: cached, isLoading: false, isOffline: true, pendingReviewCount: pending });
      return cached;
    }
    set({ isLoading: false });
    return [];
  },

  createCard: async (card) => {
    try {
      const response = await authFetch(`${API_URL}/flashcards/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(card),
      });
      if (response.ok) {
        const data = await response.json();
        return data.card;
      }
    } catch {}
    return null;
  },

  reviewCard: async (cardId, difficulty) => {
    const state = get();
    if (state.isOffline) {
      // Queue review for later sync
      const userId = state._getUserId();
      offlineStorage.addPendingReview(userId, cardId, difficulty);
      const newPending = offlineStorage.getPendingReviews(userId).length;
      // Remove card from local list
      set((s) => ({
        dueCards: s.dueCards.filter(c => c.card_id !== cardId),
        pendingReviewCount: newPending,
      }));
      return { queued: true };
    }
    try {
      const response = await authFetch(`${API_URL}/flashcards/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ card_id: cardId, difficulty }),
      });
      if (response.ok) {
        const data = await response.json();
        set((s) => ({ dueCards: s.dueCards.filter(c => c.card_id !== cardId) }));
        return data;
      }
    } catch {}
    return null;
  },

  fetchStats: async () => {
    try {
      const response = await authFetch(`${API_URL}/flashcards/stats`);
      if (response.ok) {
        const data = await response.json();
        const userId = get()._getUserId();
        if (userId) offlineStorage.saveFlashcardStats(userId, data.stats);
        set({ stats: data.stats });
        return data.stats;
      }
    } catch {
      // Offline fallback
      const userId = get()._getUserId();
      const cached = userId ? offlineStorage.loadFlashcardStats(userId) : null;
      if (cached) {
        set({ stats: cached });
        return cached;
      }
    }
    return null;
  },

  // Sync pending offline reviews when reconnected
  syncPendingReviews: async () => {
    const userId = get()._getUserId();
    if (!userId) return 0;
    const pending = offlineStorage.getPendingReviews(userId);
    if (pending.length === 0) return 0;

    let synced = 0;
    for (const { cardId, difficulty } of pending) {
      try {
        const response = await authFetch(`${API_URL}/flashcards/review`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ card_id: cardId, difficulty }),
        });
        if (response.ok) synced++;
      } catch {}
    }
    offlineStorage.clearPendingReviews(userId);
    set({ isOffline: false, pendingReviewCount: 0 });
    return synced;
  },
}));

// Admin Store
export const useAdminStore = create((set) => ({
  users: [],
  stats: null,
  chartData: [],
  isLoading: false,
  roleFilter: null,
  
  fetchUsers: async (role = null) => {
    set({ isLoading: true, roleFilter: role });
    try {
      const url = role ? `${API_URL}/admin/users?role=${role}` : `${API_URL}/admin/users`;
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        set({ users: data.users, isLoading: false });
        return data.users;
      }
    } catch (error) {}
    set({ isLoading: false });
    return [];
  },
  
  fetchStats: async () => {
    try {
      const response = await authFetch(`${API_URL}/admin/stats`);
      if (response.ok) {
        const data = await response.json();
        set({ stats: data.stats });
        return data.stats;
      }
    } catch (error) {}
    return null;
  },
  
  fetchChartData: async (days = 14) => {
    try {
      const response = await authFetch(`${API_URL}/admin/activity-chart?days=${days}`);
      if (response.ok) {
        const data = await response.json();
        set({ chartData: data.chart_data });
        return data.chart_data;
      }
    } catch (error) {}
    return [];
  },
  
  deactivateUser: async (userId) => {
    try {
      await authFetch(`${API_URL}/admin/users/${userId}/deactivate`, {
        method: 'POST',
      });
      return true;
    } catch (error) {
      return false;
    }
  },
  
  activateUser: async (userId) => {
    try {
      await authFetch(`${API_URL}/admin/users/${userId}/activate`, {
        method: 'POST',
      });
      return true;
    } catch (error) {
      return false;
    }
  },
}));

// PDF Export Store
export const usePdfStore = create(() => ({
  exportEssay: async (data) => {
    try {
      const response = await authFetch(`${API_URL}/export/essay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mentis_${data.essay_type || 'aufsatz'}_${new Date().toISOString().split('T')[0]}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        return true;
      }
    } catch (error) {
      console.error('PDF export error:', error);
    }
    return false;
  },
  
  exportMath: async (data) => {
    try {
      const response = await authFetch(`${API_URL}/export/math`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mentis_mathe_${new Date().toISOString().split('T')[0]}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        return true;
      }
    } catch (error) {
      console.error('PDF export error:', error);
    }
    return false;
  },
  
  exportFlashcards: async (data = {}) => {
    try {
      const response = await authFetch(`${API_URL}/export/flashcards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mentis_karteikarten_${new Date().toISOString().split('T')[0]}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        return true;
      }
    } catch (error) {
      console.error('PDF export error:', error);
    }
    return false;
  },
  
  exportGeneric: async (data) => {
    try {
      const response = await authFetch(`${API_URL}/export/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${data.filename || 'mentis_export'}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        return true;
      }
    } catch (error) {
      console.error('PDF export error:', error);
    }
    return false;
  },
}));

// Credit Usage Store (Founder Only)
export const useCreditStore = create((set, get) => ({
  usage: null,
  topUsers: [],
  notifications: [],
  isLoading: false,
  
  fetchUsage: async () => {
    set({ isLoading: true });
    try {
      const response = await authFetch(`${API_URL}/credits/project-usage`);
      
      if (response.ok) {
        const data = await response.json();
        set({
          usage: data.usage,
          topUsers: data.top_users || [],
          notifications: data.notifications || [],
          isLoading: false
        });
        return data;
      }
    } catch (error) {
      console.error('Credit fetch error:', error);
    }
    set({ isLoading: false });
    return null;
  },
  
  markNotificationsRead: async () => {
    try {
      await authFetch(`${API_URL}/credits/notifications/mark-read`, {
        method: 'POST',
      });
      set({ notifications: [] });
    } catch (error) {}
  },
}));

// User Daily Usage Store (Free vs Premium limit tracking)
export const useUsageStore = create((set) => ({
  usageStatus: null,
  isLoading: false,

  fetchUsageStatus: async () => {
    set({ isLoading: true });
    try {
      const response = await authFetch(`${API_URL}/user/usage`);
      if (response.ok) {
        const data = await response.json();
        set({ usageStatus: data, isLoading: false });
        return data;
      }
    } catch (error) {
      // silent — don't break UI
    }
    set({ isLoading: false });
    return null;
  },

  clearUsage: () => set({ usageStatus: null }),
}));

// Health Store
export const useHealthStore = create((set) => ({
  health: null,
  isHealthy: true,
  
  checkHealth: async () => {
    try {
      const response = await fetch(`${API_URL}/health`);
      if (response.ok) {
        const data = await response.json();
        set({ health: data, isHealthy: data.status === 'healthy' });
        return data;
      }
    } catch (error) {
      set({ isHealthy: false });
    }
    return null;
  },
}));


// ==================== GLOBAL POMODORO TIMER STORE ====================
// Timestamp-based: timer survives navigation and page reloads

export const usePomodoroStore = create(
  persist(
    (set, get) => ({
      isRunning: false,
      mode: 'focus',         // 'focus' | 'short_break' | 'long_break'
      endTs: null,           // Unix ms when timer ends
      remainingMs: null,     // Stored when paused
      subject: 'Allgemein',
      customMinutes: 25,
      sessionsCompleted: 0,
      sessionJustCompleted: false, // Flag to trigger in-app notification
      language: 'de',        // Timer language: 'de' | 'it' | 'fr' | 'es'

      setLanguage: (lang) => set({ language: lang }),

      startTimer: (durationMs, mode, subject, customMinutes) => {
        const endTs = Date.now() + durationMs;
        set({ isRunning: true, mode, endTs, remainingMs: null, subject, customMinutes: customMinutes || 25, sessionJustCompleted: false });
      },

      pauseTimer: () => {
        const { endTs } = get();
        if (!endTs) return;
        const remaining = Math.max(0, endTs - Date.now());
        set({ isRunning: false, endTs: null, remainingMs: remaining });
      },

      resumeTimer: () => {
        const { remainingMs } = get();
        if (!remainingMs) return;
        set({ isRunning: true, endTs: Date.now() + remainingMs, remainingMs: null });
      },

      stopTimer: () => {
        set({ isRunning: false, endTs: null, remainingMs: null });
      },

      completeSession: () => {
        const { mode, sessionsCompleted } = get();
        const newCount = mode === 'focus' ? sessionsCompleted + 1 : sessionsCompleted;
        // Auto-switch: after 4 focus sessions → long break, else short break
        const nextMode = mode === 'focus'
          ? (newCount % 4 === 0 ? 'long_break' : 'short_break')
          : 'focus';
        const nextMinutes = nextMode === 'focus' ? 25 : nextMode === 'short_break' ? 5 : 15;
        set({
          isRunning: false,
          endTs: null,
          remainingMs: null,
          mode: nextMode,
          customMinutes: nextMode === 'focus' ? get().customMinutes : nextMinutes,
          sessionsCompleted: newCount,
          sessionJustCompleted: true,
        });
      },

      clearJustCompleted: () => set({ sessionJustCompleted: false }),
    }),
    { name: 'mentis-pomodoro-timer' }
  )
);

// ==================== COLOR THEME STORE ====================

const THEME_NAMES = {
  'default': 'Navy',
  'sky-blue': 'Sky Blue',
  'mint': 'Mint',
  'sand': 'Sand',
  'midnight-black': 'Midnight Black',
  'wine-red': 'Wine Red',
  'lavender': 'Lavender',
  'soft-pink': 'Soft Pink',
};

const applyColorTheme = (theme) => {
  if (theme && theme !== 'default') {
    document.documentElement.setAttribute('data-color-theme', theme);
  } else {
    document.documentElement.removeAttribute('data-color-theme');
  }
};

export const useColorThemeStore = create(
  persist(
    (set, get) => ({
      colorTheme: 'default',
      THEME_NAMES,

      setColorTheme: async (theme) => {
        applyColorTheme(theme);
        set({ colorTheme: theme });
        // Save to DB
        try {
          const token = localStorage.getItem('mentis_session_token');
          if (token) {
            await authFetch(`${API_URL}/user/theme`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ theme }),
            });
          }
        } catch (e) { /* ignore DB sync errors */ }
      },

      initTheme: async () => {
        // Apply stored theme on startup
        const { colorTheme } = get();
        applyColorTheme(colorTheme);
        // Sync from DB
        try {
          const token = localStorage.getItem('mentis_session_token');
          if (token) {
            const res = await authFetch(`${API_URL}/user/theme`);
            if (res.ok) {
              const data = await res.json();
              if (data.theme && data.theme !== colorTheme) {
                applyColorTheme(data.theme);
                set({ colorTheme: data.theme });
              }
            }
          }
        } catch (e) { /* ignore */ }
      },
    }),
    { name: 'mentis-color-theme' }
  )
);


// ==================== LANGUAGE / i18n STORE ====================
// Global language management with persistence and browser detection

import { detectBrowserLanguage, createTranslator, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../utils/i18n';

export const useLanguageStore = create(
  persist(
    (set, get) => ({
      language: null, // Will be set on init
      isInitialized: false,

      // Get the translator function
      t: (key, params) => {
        const { language } = get();
        const translator = createTranslator(language || DEFAULT_LANGUAGE);
        return translator(key, params);
      },

      // Set language and save to backend
      setLanguage: async (lang) => {
        set({ language: lang });
        
        // Save to backend
        try {
          const token = localStorage.getItem('mentis_session_token');
          if (token) {
            await authFetch(`${API_URL}/user/language`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ language: lang }),
            });
          }
        } catch (e) {
          console.warn('Failed to save language to backend:', e);
        }
      },

      // Initialize language on app start
      initLanguage: async () => {
        const { language, isInitialized } = get();
        
        // If already initialized with a valid language, just return
        if (isInitialized && language) return;

        // Try to get from backend first
        try {
          const token = localStorage.getItem('mentis_session_token');
          if (token) {
            const res = await authFetch(`${API_URL}/user/language`);
            if (res.ok) {
              const data = await res.json();
              if (data.language && SUPPORTED_LANGUAGES.some(l => l.code === data.language)) {
                set({ language: data.language, isInitialized: true });
                return;
              }
            }
          }
        } catch (e) {
          console.warn('Failed to fetch language from backend:', e);
        }

        // If no backend language, check localStorage or detect browser
        if (!language) {
          const detectedLang = detectBrowserLanguage();
          set({ language: detectedLang, isInitialized: true });
        } else {
          set({ isInitialized: true });
        }
      },
    }),
    { 
      name: 'mentis-language',
      partialize: (state) => ({ language: state.language }), // Only persist language
    }
  )
);

// Custom hook for easy translation access
export const useTranslation = () => {
  const { language, t, setLanguage } = useLanguageStore();
  return { language, t, setLanguage };
};

// ==================== PREMIUM STORE ====================

/**
 * checkIfUserIsPremium — pure client-side check (mirrors backend check_if_user_is_premium).
 *
 * Rules:
 *   - isPremium must be true
 *   - expiresAt = null → lifetime (no expiry) → true
 *   - expiresAt in the future → true
 *   - expiresAt in the past  → false (expired)
 *
 * Use this EVERYWHERE instead of reading isPremium directly.
 * Exported so PremiumGate, PremiumPage, Sidebar, etc. all share the same logic.
 */
export const checkIfUserIsPremium = (premiumState) => {
  if (!premiumState) return false;
  if (!premiumState.isPremium) return false;
  const { expiresAt } = premiumState;
  if (!expiresAt) return true; // lifetime — no expiry
  try {
    return new Date(expiresAt) > new Date();
  } catch {
    return false;
  }
};

export const usePremiumStore = create(
  persist(
    (set, get) => ({
      isPremium: false,
      plan: null,       // "monthly" | "yearly" | "lifetime" | "reward_*" | null
      source: null,     // "mock" | "stripe" | "apple" | "google" | "whitelist" | "streak" | "referral"
      expiresAt: null,  // ISO string or null (null = lifetime)
      daysRemaining: 0,
      prices: { monthly: 4.99, yearly: 39.99, lifetime: 79.99 },
      progress: null,
      isLoading: false,
      lastChecked: null,

      // Computed: true only if premium AND not expired (client-side expiry check)
      get isActive() {
        return checkIfUserIsPremium(get());
      },

      fetchStatus: async () => {
        set({ isLoading: true });
        try {
          const response = await authFetch(`${API_URL}/premium/status`);
          if (response.ok) {
            const text = await response.text();
            if (!text) { set({ isLoading: false }); return null; }
            let data;
            try { data = JSON.parse(text); }
            catch {
              console.warn('[PremiumStore] Invalid JSON from /premium/status');
              set({ isLoading: false });
              return null;
            }
            // Client-side expiry check: if server says premium but date is past, treat as free
            const serverIsPremium = data.is_premium || false;
            const expiresAt = data.expires_at || null;
            const isActuallyPremium = checkIfUserIsPremium({
              isPremium: serverIsPremium,
              expiresAt,
            });
            if (serverIsPremium && !isActuallyPremium) {
              console.warn('[PremiumStore] Server returned isPremium but token is expired client-side');
            }
            set({
              isPremium: isActuallyPremium,
              plan: data.plan || null,
              source: data.source || null,
              expiresAt,
              daysRemaining: data.days_remaining || 0,
              prices: data.prices || { monthly: 4.99, yearly: 39.99, lifetime: 79.99 },
              progress: data.progress || null,
              isLoading: false,
              lastChecked: Date.now(),
            });
            return data;
          } else {
            console.warn('[PremiumStore] fetchStatus HTTP', response.status);
          }
        } catch {
          console.warn('[PremiumStore] fetchStatus network error, using cached state');
        }
        set({ isLoading: false });
        return null;
      },

      /**
       * initiatePayment — platform-aware payment routing.
       *
       * PAYMENT INTEGRATION PENDING:
       *   "web"     → future: Stripe Checkout redirect
       *   "ios"     → future: RevenueCat iOS SDK
       *   "android" → future: RevenueCat Android SDK
       *
       * Currently returns comingSoon=true without activating premium.
       * Real payment logic will replace this block when Stripe is integrated.
       */
      initiatePayment: async (platform, plan) => {
        console.log(`[PremiumStore] initiatePayment | platform=${platform} | plan=${plan} | status=coming_soon`);
        // Payments not yet integrated — do NOT activate premium
        return { comingSoon: true };
      },

      upgrade: async (plan) => {
        set({ isLoading: true });
        try {
          const response = await authFetch(`${API_URL}/premium/upgrade`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ plan, source: 'mock' }),
          });
          if (response.ok) {
            const text = await response.text();
            let data;
            try { data = JSON.parse(text); }
            catch {
              console.warn('[PremiumStore] Invalid JSON from /premium/upgrade');
              set({ isLoading: false });
              return null;
            }
            set({
              isPremium: true,
              plan: data.plan,
              source: 'mock',
              expiresAt: data.expires_at,
              daysRemaining: data.days,
              isLoading: false,
              lastChecked: Date.now(),
            });
            console.log(`[PremiumStore] Upgrade success | plan=${data.plan} | expires=${data.expires_at}`);
            return data;
          }
        } catch (err) {
          console.warn('[PremiumStore] upgrade error:', err.message);
        }
        set({ isLoading: false });
        return null;
      },
    }),
    {
      name: 'mentis-premium',
      partialize: (state) => ({
        isPremium: state.isPremium,
        plan: state.plan,
        source: state.source,
        expiresAt: state.expiresAt,
        daysRemaining: state.daysRemaining,
      }),
    }
  )
);
